package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.MtEstimateDto
import dev.lingvo.shared.MtJobDto
import dev.lingvo.shared.MtJobState
import dev.lingvo.shared.MtPretranslateRequest
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ProviderReadiness
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LHistoryIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LSettingsIcon
import dev.lingvo.site.ds.LStatDelta
import dev.lingvo.site.ds.LStatTile
import dev.lingvo.site.ds.LSwitch
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LToggleChip
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.ds.LZapIcon
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.nav.UrlState
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal data class PretranslateData(
    val project: ProjectDetailDto,
    val providers: List<ProviderSettingsDto>,
)

/**
 * What the run was asked to cover, as it arrived in the URL.
 *
 * Every automation elsewhere in the product — the readiness row, the editor queue, the dashboard
 * card, the command palette — hands this screen a *slice*: one tag, one file, or the keys a filter
 * matched. Without it the estimate and the run would both quietly widen to the whole project, which
 * is the difference between "pre-translate the 412 untranslated strings in German" and buying a
 * translation of everything.
 */
internal data class RunScope(
    val tag: String?,
    val fileId: String?,
    val keyIds: List<String>,
) {
    val narrowed: Boolean get() = tag != null || fileId != null || keyIds.isNotEmpty()
}

@Page("/app/projects/{slug}/pretranslate")
@Layout(".components.layouts.AppLayout")
@Composable
fun PretranslatePage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    var refreshKey by remember { mutableStateOf(0) }
    val dataState = loadAsync(slug, refreshKey) {
        PretranslateData(ApiClient.getProject(slug), ApiClient.listProviders(slug))
    }
    val toasts = rememberToasts()
    val verifiedRoute = liveProjectRoute(slug)
    val authority = ProjectAuthority.resolve(verifiedRoute?.project, Session.organizations)
    // Spending the workspace's machine-translation credits has its own privilege, so a claim that
    // pays for a run reads as a spend at the line that takes it. The claim is taken here, before any
    // control is drawn, so a role that changes underneath the page cannot be used by a click that
    // was already in flight.
    val spendClaim = ProjectActionClaim.capture(
        verifiedRoute,
        Session.organizations,
        ProjectPrivilege.SPEND,
    )

    Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
        PageHeader(
            t("app.projects.slug.pretranslate.pre_translate", "Pre-translate"),
            t("app.projects.slug.pretranslate.translate_batches_with_an_enabled_provider", "Translate batches with an enabled provider, then review every result before release."),
            actions = {
                // The runs list is the history of this screen's spending and had no inbound link
                // anywhere in the product either: the receipt's "All runs" pointed at a page nothing
                // pointed at. This is its parent — the runs it lists are the runs started here.
                LButton(
                    t("app.projects.slug.pretranslate.all_runs", "All runs"),
                    { ctx.router.tryRoutingTo("/app/projects/$slug/data/jobs") },
                    variant = LButtonVariant.SECONDARY,
                    leadingIcon = { LHistoryIcon() },
                )
                LButton(
                    t("app.projects.slug.pretranslate.provider_settings", "Provider settings"),
                    { ctx.router.tryRoutingTo("/app/projects/$slug/settings?tab=providers") },
                    variant = LButtonVariant.SECONDARY,
                    leadingIcon = { LSettingsIcon() },
                )
            },
        )
        LAsyncContent(dataState, rows = 5, onRetry = { refreshKey++ }) { loaded ->
            // `PretranslateContent` emits the plan card and the live job card as siblings, and
            // this page's column is what spaced them apart. The content slot is one plain
            // `<div>`, so the same gap has to be reinstated inside it.
            Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
                // **This page had no authority check of any kind.** `MtRoutes` requires an owner or
                // admin to start a run, because a run spends the workspace's credits — and the whole
                // form drew for a translator, who chose a provider, priced a batch and only met the
                // refusal after pressing the button. Gated the way `Data.kt` gates its import half.
                if (!authority.canEditStructure || spendClaim == null) {
                    LAlert(
                        title = t(
                            "app.projects.slug.pretranslate.pre_translation_is_managed_by_an",
                            "Pre-translation is managed by an owner or admin",
                        ),
                        description = t("app.projects.slug.pretranslate.a_run_spends_this_workspaces_credits_2", "A run spends this workspace's credits, so an owner or admin " +
                            "has to start one. You can still translate and review every string it " +
                            "produces."),
                        variant = LAlertVariant.INFO,
                    )
                } else {
                    PretranslateContent(
                        slug = slug,
                        data = loaded,
                        claim = spendClaim,
                        scope = RunScope(
                            tag = UrlState.read(ctx, "tag"),
                            fileId = UrlState.read(ctx, "fileId"),
                            keyIds = UrlState.read(ctx, "keyIds")
                                ?.split(',')
                                ?.map(String::trim)
                                ?.filter(String::isNotEmpty)
                                .orEmpty(),
                        ),
                        requestedProvider = UrlState.read(ctx, "provider")?.let(MtProvider::fromId),
                        // `locale` is the singular spelling of the same parameter, and it is the one
                        // every deep link into this page actually uses: the dashboard's readiness
                        // card, the projects-page card menu, `/app/work`'s launcher rows and the
                        // readiness band all link to `?locale={code}` because each of them offers
                        // exactly one language and states that language's own count. Reading only
                        // the plural spelling meant those four links arrived here with every locale
                        // ticked — so the run this page priced was wider than the number the reader
                        // pressed, which is the one thing an estimate exists to prevent. The page
                        // rewrites the query with the plural spelling once it settles, so the two
                        // never both appear.
                        requestedLocales = (UrlState.read(ctx, "locales") ?: UrlState.read(ctx, "locale"))
                            ?.split(',')
                            ?.map(String::trim)
                            ?.filter(String::isNotEmpty)
                            .orEmpty(),
                        requestedOnlyEmpty = UrlState.read(ctx, "onlyEmpty")?.toBooleanStrictOrNull(),
                        requestedMemoryOnly = UrlState.read(ctx, "memoryOnly")?.toBooleanStrictOrNull() == true,
                        attachToJobId = UrlState.read(ctx, "job"),
                        toasts = toasts,
                        onOpenReceipt = { jobId ->
                            ctx.router.tryRoutingTo("/app/projects/$slug/data/jobs/$jobId")
                        },
                        onOpenProviders = {
                            ctx.router.tryRoutingTo("/app/settings/providers")
                        },
                        // The project's own languages, not the workspace's providers. The empty
                        // state below invites exactly one action — "Open language settings" — and
                        // the workspace provider page has no language control on it at all, so the
                        // one thing this screen offers a project with no targets could not do the
                        // thing it names. Same route the readiness band uses.
                        onOpenLanguages = {
                            ctx.router.tryRoutingTo("/app/projects/$slug/settings?tab=languages")
                        },
                    )
                }
            }
        }
    }
    ToastHost(toasts)
}

@Composable
internal fun PretranslateContent(
    slug: String,
    data: PretranslateData,
    claim: ProjectActionClaim,
    scope: RunScope = RunScope(null, null, emptyList()),
    requestedProvider: MtProvider? = null,
    requestedLocales: List<String> = emptyList(),
    requestedOnlyEmpty: Boolean? = null,
    requestedMemoryOnly: Boolean = false,
    attachToJobId: String? = null,
    toasts: ToastState,
    onOpenReceipt: (String) -> Unit,
    onOpenProviders: () -> Unit,
    /** Where "Open language settings" goes: this project's languages, not workspace providers. */
    onOpenLanguages: () -> Unit,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    estimateRun: suspend (String, MtPretranslateRequest) -> MtEstimateDto = ApiClient::mtEstimate,
    startRun: suspend (String, MtPretranslateRequest) -> String = ApiClient::pretranslate,
    cancelRun: suspend (String, String) -> MtJobDto = ApiClient::cancelMtJob,
    watchRun: suspend (String, String, (MtJobDto) -> Unit) -> Unit = { projectSlug, jobId, onUpdate ->
        ApiClient.pollJob(projectSlug, jobId).collect(onUpdate)
    },
) {
    // **`readiness`, not `enabled`.** A key that will not decrypt is stored, enabled and unusable;
    // the old filter offered it, priced a batch against it and met the refusal at the button.
    val ready = data.providers.filter { it.readiness == ProviderReadiness.READY }
    val needsSetup = data.providers.filter { it.readiness == ProviderReadiness.NEEDS_SETUP }
    if (ready.isEmpty()) {
        LEmptyState(
            t("app.projects.slug.pretranslate.no_provider_is_ready", "No provider is ready"),
            t("app.projects.slug.pretranslate.enable_mymemory_or_libretranslate_for_a", "Enable MyMemory or LibreTranslate for a no-key start, or connect an AI provider."),
            Modifier.fillMaxWidth(),
            LEmptyStateIcon.AUTOMATION,
            secondaryAction = {
                needsSetup.firstOrNull()?.let { provider ->
                    LMuted(
                        provider.unavailableReason
                            ?: trf(
                                "app.projects.slug.pretranslate.provider_needs_setup",
                                "{provider} is configured but not usable yet.",
                                "provider" to provider.provider.displayName,
                            ),
                    )
                }
            },
        ) {
            LButton(
                t("app.projects.slug.pretranslate.open_provider_settings", "Open provider settings"),
                onOpenProviders,
                leadingIcon = { LSettingsIcon() },
            )
        }
        return
    }
    // Never offer the source locale as a machine-translation target: translating English into
    // English burns provider quota and overwrites the strings developers actually wrote.
    val targetLocales = data.project.locales.filterNot { it == data.project.sourceLocale }
    if (targetLocales.isEmpty()) {
        LEmptyState(
            t("app.projects.slug.pretranslate.no_target_languages", "No target languages"),
            t("app.projects.slug.pretranslate.add_a_target_language_before_starting", "Add a target language before starting pre-translation."),
            Modifier.fillMaxWidth(),
            LEmptyStateIcon.LANGUAGE,
        ) {
            LButton(
                t("app.projects.slug.pretranslate.open_language_settings", "Open language settings"),
                onOpenLanguages,
            )
        }
        return
    }

    val coroutineScope = rememberCoroutineScope()
    // Behind by the most, first. Somebody who opens this screen is looking for the language that is
    // furthest from shippable; alphabetical order buried it under whichever locale starts with "a".
    val untranslatedByLocale = targetLocales.associateWith { code ->
        val row = data.project.progress.firstOrNull { it.locale == code }
        val total = row?.total ?: data.project.keyCount
        (total - (row?.translated ?: 0)).coerceAtLeast(0)
    }
    val orderedLocales = targetLocales.sortedWith(
        compareByDescending<String> { untranslatedByLocale[it] ?: 0 }
            .thenBy { localeInfo(it).englishName },
    )

    var providerId by remember(ready, requestedProvider) {
        mutableStateOf(
            requestedProvider?.takeIf { wanted -> ready.any { it.provider == wanted } }?.id
                ?: ready.first().provider.id,
        )
    }
    var selectedLocales by remember(orderedLocales, requestedLocales) {
        mutableStateOf(
            requestedLocales.filter { it in targetLocales }.toSet().ifEmpty { targetLocales.toSet() },
        )
    }
    var onlyEmpty by remember(requestedOnlyEmpty) { mutableStateOf(requestedOnlyEmpty ?: true) }
    var memoryOnly by remember(requestedMemoryOnly) { mutableStateOf(requestedMemoryOnly) }
    var tone by remember { mutableStateOf("default") }
    var starting by remember { mutableStateOf(false) }
    var cancelling by remember { mutableStateOf(false) }
    var confirmCancel by remember { mutableStateOf(false) }
    var confirmOverwrite by remember { mutableStateOf(false) }
    var jobId by remember(attachToJobId) { mutableStateOf(attachToJobId) }
    var job by remember(attachToJobId) { mutableStateOf<MtJobDto?>(null) }
    var jobError by remember { mutableStateOf<String?>(null) }
    var pollAttempt by remember { mutableStateOf(0) }
    var estimate by remember { mutableStateOf<MtEstimateDto?>(null) }
    var estimating by remember { mutableStateOf(false) }
    var estimateError by remember { mutableStateOf<String?>(null) }

    val provider = MtProvider.fromId(providerId)
    // **`abandoned` is read here, not only on the receipt.** A run killed with the process stays
    // RUNNING for ever while the reaper has already refunded it, so the old predicate left this
    // form disabled behind a spinner that could never end.
    val watched = job
    val running = starting || (
        jobId != null && (watched == null || (watched.state == MtJobState.RUNNING && !watched.abandoned))
        )

    // Held, not rebuilt: the estimate below and the start above post the *same* object, so the
    // number on the button and the number the server charges cannot drift apart.
    val request = remember(provider, selectedLocales, onlyEmpty, tone, memoryOnly, scope) {
        provider?.let {
            MtPretranslateRequest(
                provider = it,
                locales = selectedLocales.toList().sorted(),
                onlyEmpty = onlyEmpty,
                tone = tone.takeUnless { chosen -> chosen == "default" },
                tag = scope.tag,
                fileId = scope.fileId,
                keyIds = scope.keyIds,
                memoryOnly = memoryOnly,
            )
        }
    }

    LaunchedEffect(request, slug) {
        val planned = request?.takeIf { it.locales.isNotEmpty() }
        estimate = null
        estimateError = null
        if (planned == null) return@LaunchedEffect
        estimating = true
        try {
            estimate = requestProjectAction(
                claim = claim,
                currentRoute = currentRoute,
                currentMemberships = currentMemberships,
            ) { estimateRun(slug, planned) }
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            estimateError = failure.safeMessage(
                tr(
                    "app.projects.slug.pretranslate.couldnt_price_this_run",
                    "Couldn't price this run. Change the selection, or retry.",
                ),
            )
        } finally {
            estimating = false
        }
    }

    val attach: (String) -> Unit = { id ->
        jobId = id
        job = null
        // So the receipt survives a reload. Somebody who refreshes the tab while a run is going used
        // to land back on an empty form with no way to find the run they had just started. The rest
        // of the query travels with it, or the reload would show a default plan beside the real run.
        UrlState.replace(
            mapOf(
                "job" to id,
                "provider" to providerId,
                "locales" to selectedLocales.sorted().joinToString(","),
                "onlyEmpty" to onlyEmpty.toString(),
                "memoryOnly" to memoryOnly.takeIf { it }?.toString(),
                "tag" to scope.tag,
                "fileId" to scope.fileId,
                "keyIds" to scope.keyIds.takeIf(List<String>::isNotEmpty)?.joinToString(","),
            ),
        )
    }

    val startJob: () -> Unit = {
        val planned = request
        if (planned != null) {
            coroutineScope.launch {
                starting = true
                jobError = null
                try {
                    val started = requestProjectAction(
                        claim = claim,
                        currentRoute = currentRoute,
                        currentMemberships = currentMemberships,
                    ) { startRun(slug, planned) }
                    attach(started)
                    toasts.showWithAction(
                        message = tr("app.projects.slug.pretranslate.pre_translation_started", "Pre-translation started"),
                        actionLabel = tr("app.projects.slug.pretranslate.open_the_receipt", "Open the receipt"),
                        onAction = { onOpenReceipt(started) },
                        variant = LToastVariant.INFO,
                    )
                } catch (cancelled: CancellationException) {
                    // Navigating away while the run was starting used to land a six-second red
                    // toast over a dead screen, for a job that had almost certainly started.
                    throw cancelled
                } catch (failure: Throwable) {
                    // **A duplicate run is not a failure.** The server refuses a second run over
                    // locales somebody is already paying for and hands back the one that is going;
                    // reporting that as an error taught people to press the button again.
                    val existing = failure.runningJobId()
                    if (existing != null) {
                        attach(existing)
                        toasts.showWithAction(
                            message = tr(
                                "app.projects.slug.pretranslate.a_run_is_already_going_for",
                                "A run is already going for these languages. Showing that one.",
                            ),
                            actionLabel = tr("app.projects.slug.pretranslate.open_the_receipt", "Open the receipt"),
                            onAction = { onOpenReceipt(existing) },
                            variant = LToastVariant.INFO,
                        )
                    } else {
                        jobError = failure.safeMessage(
                            tr(
                                "app.projects.slug.pretranslate.couldnt_start_pre_translation",
                                "Couldn't start pre-translation. Check the provider and retry.",
                            ),
                        )
                        toasts.show(jobError.orEmpty(), LToastVariant.DANGER, TOAST_UNTIL_DISMISSED)
                    }
                } finally {
                    starting = false
                }
            }
        }
    }

    LaunchedEffect(jobId, pollAttempt) {
        val activeJobId = jobId ?: return@LaunchedEffect
        jobError = null
        try {
            watchRun(slug, activeJobId) { update -> job = update }
            val terminal = job
            when {
                terminal == null -> Unit
                terminal.cancelled -> Unit
                terminal.state == MtJobState.DONE -> toasts.showWithAction(
                    message = tr("app.projects.slug.pretranslate.pre_translation_finished", "Pre-translation finished"),
                    actionLabel = tr("app.projects.slug.pretranslate.open_the_receipt", "Open the receipt"),
                    onAction = { onOpenReceipt(activeJobId) },
                    variant = LToastVariant.SUCCESS,
                )
                terminal.state == MtJobState.FAILED -> toasts.showWithAction(
                    message = tr("app.projects.slug.pretranslate.pre_translation_stopped", "Pre-translation stopped"),
                    actionLabel = tr("app.projects.slug.pretranslate.open_the_receipt", "Open the receipt"),
                    onAction = { onOpenReceipt(activeJobId) },
                    variant = LToastVariant.DANGER,
                )
                else -> Unit
            }
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            jobError = failure.safeMessage(
                tr(
                    "app.projects.slug.pretranslate.couldnt_refresh_job_progress",
                    "Couldn't refresh run progress. Retry the run view.",
                ),
            )
        }
    }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().maxWidth(54.cssRem).gap(Space.LG)) {
            LSectionHeading(
                t("app.projects.slug.pretranslate.translation_plan", "Translation plan"),
                t("app.projects.slug.pretranslate.choose_where_machine_output_should_be", "Choose where machine output should be created."),
                icon = { LWandSparklesIcon() },
            )
            if (scope.narrowed) {
                LAlert(
                    t("app.projects.slug.pretranslate.this_run_covers_a_selection", "This run covers a selection"),
                    scope.sentence(),
                    variant = LAlertVariant.INFO,
                    dense = true,
                )
            }
            // Bottom-aligned. `LSelect` draws its own PROVIDER label above the control, so with
            // Kobweb's default top alignment "Set it up" and the provider badge lined up with the
            // top of that *label* — floating some 20px above the 48px box they belong to, and not
            // level with each other either. Flush with the bottom of the control, the three read
            // as one row.
            Row(
                Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.Bottom,
            ) {
                LSelect(
                    providerId,
                    ready.map { LSelectOption(it.provider.id, it.provider.providerLabel()) } +
                        needsSetup.map { setup ->
                            LSelectOption(
                                setup.provider.id,
                                trf(
                                    "app.projects.slug.pretranslate.provider_not_ready_option",
                                    "{provider} — {reason}",
                                    "provider" to setup.provider.displayName,
                                    "reason" to (
                                        setup.unavailableReason
                                            ?: tr("app.projects.slug.pretranslate.needs_setup", "needs setup")
                                        ),
                                ),
                                disabled = true,
                            )
                        },
                    { providerId = it },
                    Modifier.maxWidth(26.cssRem),
                    t("app.projects.slug.pretranslate.provider", "Provider"),
                    enabled = !running,
                )
                if (needsSetup.isNotEmpty()) {
                    LButton(
                        t("app.projects.slug.pretranslate.set_it_up", "Set it up"),
                        onOpenProviders,
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                        leadingIcon = { LSettingsIcon() },
                    )
                }
                provider?.let {
                    LBadge(
                        if (it.isAi()) {
                            t("app.projects.slug.pretranslate.context_aware_ai", "Context-aware AI")
                        } else {
                            t("app.projects.slug.pretranslate.machine_translation", "Machine translation")
                        },
                        variant = if (it.isAi()) LBadgeVariant.ACCENT else LBadgeVariant.INFO,
                    )
                }
            }
            LocaleChoice(
                locales = orderedLocales,
                untranslated = untranslatedByLocale,
                selected = selectedLocales,
                enabled = !running,
                onChange = { selectedLocales = it },
            )
            LSwitch(
                onlyEmpty,
                { onlyEmpty = it },
                t("app.projects.slug.pretranslate.only_untranslated_keys", "Only untranslated keys"),
                enabled = !running,
            )
            // **The free automation, offered as one.** Filling from the workspace's own memory calls
            // no provider and reserves nothing, and until it was a control on this screen the only
            // way to ask for it was the API.
            LSwitch(
                memoryOnly,
                { memoryOnly = it },
                t("app.projects.slug.pretranslate.fill_from_translation_memory_only", "Fill from translation memory only"),
                enabled = !running,
            )
            if (provider?.supportsTone() == true && !memoryOnly) {
                LSelect(
                    tone,
                    listOf(
                        LSelectOption("default", t("app.projects.slug.pretranslate.default", "Default")),
                        LSelectOption("formal", t("app.projects.slug.pretranslate.formal", "Formal")),
                        LSelectOption("casual", t("app.projects.slug.pretranslate.casual", "Casual")),
                    ),
                    { tone = it },
                    Modifier.maxWidth(22.cssRem),
                    t("app.projects.slug.pretranslate.tone", "Tone"),
                    enabled = !running,
                )
            }

            EstimateRow(estimate, estimating, estimateError, memoryOnly)

            val plannedStrings = estimate?.perLocale?.sumOf { it.strings } ?: 0
            val translatedInSelection = selectedLocales.sumOf { code ->
                data.project.progress.firstOrNull { it.locale == code }?.translated ?: 0
            }
            LButton(
                if (estimate == null) {
                    t("app.projects.slug.pretranslate.start_pre_translation", "Start pre-translation")
                } else {
                    tPlural(
                        "app.projects.slug.pretranslate.pre_translate_count_strings",
                        plannedStrings,
                        "Pre-translate {count} string",
                        "Pre-translate {count} strings",
                    )
                },
                {
                    // The overwrite confirmation asks only when there is something to overwrite, and
                    // says how much. A danger alert that is sometimes false is one people learn to
                    // click straight through.
                    if (onlyEmpty || translatedInSelection == 0) startJob() else confirmOverwrite = true
                },
                // Never before the server has priced it. Spending is the one action on this page and
                // the estimate is the only thing that says what it costs.
                enabled = selectedLocales.isNotEmpty() && !running && estimate != null &&
                    estimate?.blockedReason == null && plannedStrings > 0,
                loading = starting,
                leadingIcon = { if (memoryOnly) LZapIcon() else LWandSparklesIcon() },
            )
            if (estimate != null && plannedStrings == 0) {
                LMuted(
                    t(
                        "app.projects.slug.pretranslate.nothing_in_this_selection_needs_translating",
                        "Nothing in this selection needs translating.",
                    ),
                )
            }
            jobError?.let {
                LAlert(
                    t("app.projects.slug.pretranslate.job_update_failed", "Run update failed"),
                    it,
                    variant = LAlertVariant.DANGER,
                    action = if (jobId != null) {
                        {
                            LButton(
                                t("app.projects.slug.pretranslate.resume_progress", "Resume progress"),
                                {
                                    jobError = null
                                    pollAttempt++
                                },
                                variant = LButtonVariant.GHOST,
                                size = LButtonSize.SM,
                            )
                        }
                    } else {
                        null
                    },
                )
            }
        }
    }

    watched?.let { live ->
        PretranslateRunCard(
            job = live,
            onOpenReceipt = { onOpenReceipt(live.id) },
            onCancel = { confirmCancel = true }.takeIf { live.state == MtJobState.RUNNING && !live.abandoned },
            cancelling = cancelling,
        )
    }

    LConfirmDialog(
        open = confirmOverwrite,
        title = t("app.projects.slug.pretranslate.replace_existing_translations", "Replace existing translations?"),
        confirmLabel = t("app.projects.slug.pretranslate.start_and_overwrite", "Start and overwrite"),
        onConfirm = {
            confirmOverwrite = false
            startJob()
        },
        onCancel = { confirmOverwrite = false },
        consequenceTitle = t(
            "app.projects.slug.pretranslate.existing_translations_may_be_replaced",
            "Existing translations may be replaced",
        ),
        consequence = tPlural(
            "app.projects.slug.pretranslate.count.already_translated",
            selectedLocales.sumOf { code ->
                data.project.progress.firstOrNull { it.locale == code }?.translated ?: 0
            },
            "{count} string in the selected languages is already translated. Machine output can overwrite what is there now.",
            "{count} strings in the selected languages are already translated. Machine output can overwrite what is there now.",
        ),
    )

    LConfirmDialog(
        open = confirmCancel,
        title = t("app.projects.slug.pretranslate.stop_this_run", "Stop this run?"),
        confirmLabel = t("app.projects.slug.pretranslate.stop_the_run", "Stop the run"),
        onConfirm = {
            val stopping = jobId
            confirmCancel = false
            if (stopping != null) {
                coroutineScope.launch {
                    cancelling = true
                    try {
                        job = requestProjectAction(
                            claim = claim,
                            currentRoute = currentRoute,
                            currentMemberships = currentMemberships,
                        ) { cancelRun(slug, stopping) }
                        toasts.showWithAction(
                            message = tr("app.projects.slug.pretranslate.run_stopped", "Run stopped"),
                            actionLabel = tr("app.projects.slug.pretranslate.open_the_receipt", "Open the receipt"),
                            onAction = { onOpenReceipt(stopping) },
                            variant = LToastVariant.NEUTRAL,
                        )
                    } catch (cancelled: CancellationException) {
                        throw cancelled
                    } catch (failure: Throwable) {
                        toasts.show(
                            failure.safeMessage(
                                tr(
                                    "app.projects.slug.pretranslate.couldnt_stop_this_run",
                                    "Couldn't stop this run. It may have finished on its own.",
                                ),
                            ),
                            LToastVariant.DANGER,
                            TOAST_UNTIL_DISMISSED,
                        )
                    } finally {
                        cancelling = false
                    }
                }
            }
        },
        onCancel = { confirmCancel = false },
        tone = LConfirmTone.DANGER,
        consequenceTitle = t(
            "app.projects.slug.pretranslate.the_strings_it_already_wrote_stay",
            "The strings it already wrote stay",
        ),
        consequence = t(
            "app.projects.slug.pretranslate.credits_come_back_only_for_what_2",
            "Credits come back only for what it did not translate — the strings it already " +
                "delivered are in the project and stay paid for.",
        ),
        busy = cancelling,
    )
}

private fun RunScope.sentence(): String = when {
    keyIds.isNotEmpty() -> tPlural(
        "app.projects.slug.pretranslate.count.hand_picked_keys",
        keyIds.size,
        "{count} key you picked, and nothing else.",
        "{count} keys you picked, and nothing else.",
    )
    tag != null -> trf(
        "app.projects.slug.pretranslate.only_keys_tagged",
        "Only keys tagged {tag}.",
        "tag" to tag,
    )
    else -> tr("app.projects.slug.pretranslate.only_keys_in_one_file", "Only the keys in one file.")
}

private fun MtProvider.isAi(): Boolean = this == MtProvider.CLAUDE || this == MtProvider.OPENAI

/** DeepL takes a formality argument; the AI providers take an instruction. Nothing else does. */
private fun MtProvider.supportsTone(): Boolean = isAi() || this == MtProvider.DEEPL

private fun MtProvider.providerLabel(): String = if (isAi()) {
    trf("app.projects.slug.pretranslate.provider_ai", "{provider} · AI", "provider" to displayName)
} else {
    trf(
        "app.projects.slug.pretranslate.provider_machine",
        "{provider} · Machine translation",
        "provider" to displayName,
    )
}

/**
 * The run this request collided with, or null when the failure was something else.
 *
 * `409 job_already_running` is the server saying "somebody is already paying for these locales" and
 * handing back the job id. It is an instruction to attach, not an error to render.
 */
private fun Throwable.runningJobId(): String? {
    val api = this as? ApiException ?: return null
    if (api.code != "job_already_running") return null
    return api.details["jobId"]?.toString()?.trim('"')?.takeIf(String::isNotBlank)
}

/**
 * The server's sentence, or this page's own — never an internal one.
 *
 * Cast first, the way `Keys.kt` and `Glossary.kt` do: a `check`/`require` failure inside a
 * mutation lambda is a `Throwable` with a message written for a developer, and the unguarded form
 * put "Required value was null." in front of the reader.
 */
private fun Throwable.safeMessage(fallback: String): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank) ?: fallback
